import React, { useState } from 'react';
import { X, AlertTriangle, Building2, User, Briefcase, Crown, Share2, Users } from 'lucide-react';
import { Relation } from './RelationCard';
import ErrorMessage from '../../ErrorMessage';

interface DeleteRelationModalProps {
  isOpen: boolean;
  onClose: () => void;
  relation: Relation | null;
  onConfirm: () => void;
  isDeleting?: boolean;
  error?: unknown;
}

const getRelationKindLabel = (kind: string) => {
  const labels: Record<string, string> = {
    director: 'Directeur',
    executive: 'Dirigeant',
    shareholder: 'Actionnaire',
    ubo: 'UBO',
    other: 'Autre',
  };
  return labels[kind] || kind;
};

const getRelationKindIcon = (kind: string) => {
  switch (kind) {
    case 'director':
      return Briefcase;
    case 'executive':
      return Crown;
    case 'shareholder':
      return Share2;
    case 'ubo':
      return Users;
    default:
      return User;
  }
};

const DeleteRelationModal: React.FC<DeleteRelationModalProps> = ({ 
  isOpen, 
  onClose, 
  relation,
  onConfirm,
  isDeleting = false,
  error
}) => {
  const [confirmText, setConfirmText] = useState('');
  
  const handleConfirm = () => {
    if (confirmText.toLowerCase() !== 'confirmer') {
      return;
    }
    onConfirm();
    setConfirmText('');
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  if (!isOpen || !relation) return null;

  const RelationIcon = getRelationKindIcon(relation.relation_kind);
  const EntityIcon = relation.entity_type === 'company' ? Building2 : User;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">Confirmer la suppression</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Error Message */}
          {error && (
            <ErrorMessage error={error} className="mb-4" />
          )}

          <div className="mb-4">
            <p className="text-gray-700 mb-3">
              Vous êtes sur le point de supprimer la relation suivante :
            </p>
            
            {/* Relation Card Preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RelationIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <EntityIcon className="h-4 w-4 text-gray-400" />
                    <p className="font-semibold text-gray-900 break-words">
                      {relation.entity_name}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 flex-wrap">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {getRelationKindLabel(relation.relation_kind)}
                    </span>
                    {relation.percentage !== undefined && relation.percentage !== null && (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {relation.percentage}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">Attention !</h4>
                <p className="text-red-700 text-sm mt-1">
                  Cette action est irréversible. La relation sera définitivement supprimée.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pour confirmer, tapez <span className="font-semibold">"confirmer"</span> ci-dessous :
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmText.toLowerCase() === 'confirmer' && !isDeleting) {
                  handleConfirm();
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="confirmer"
              disabled={isDeleting}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isDeleting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirmText.toLowerCase() !== 'confirmer' || isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Suppression...</span>
                </>
              ) : (
                <span>Supprimer la relation</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteRelationModal;

